import * as yup from 'yup';
import { useTranslation } from 'react-i18next';
import { FormConfig } from '@dxb-web-ui-framework/content-composer/components/FormBuilder/types';
import { getClientTimezone } from '@dxb-web-ui-framework/common/utils/Date';
import { useLanguage } from '../../../../internationalization/src/contexts/Internationalization/hooks';
import {
  dateRangeFormatter,
  amountFormatter,
} from '../../../../content-composer/src/components/Generic/SelectedFiltersChip/ValueFormatters/ValueFormatters';
import { useSelectedCard } from '../../contexts/CardDetails';
import { useCardTransactionsFilters } from '../../contexts/CardDetails';

export const STATUS_DEFAULT_VALUE = '-1';

const useCardTransactionsFiltersConfig = (): FormConfig => {
  const { t } = useTranslation();
  const { cultureName } = useLanguage();
  const timezone = getClientTimezone();
  const dateFormat = t('global.settings.date.format');
  const selectedCard = useSelectedCard();
  const filters = useCardTransactionsFilters();

  const amountSchema = yup
    .string()
    .test('validFormat', t('CardTransactionsFilter.Validation.ValidFormat'), value =>
      value ? /^\d+(\.\d+)|\s?$/.test(value) : true,
    );

  return {
    validationSchema: {
      amountFrom: amountSchema,
      amountTo: amountSchema,
    },
    components: [
      {
        key: 'transactionDate',
        type: 'datePickerRange',
        name: 'transactionDate',
        label: t('cardDetailsPage.CardTransactions.CardTransactionsFiltersDrawer.FormField.DateRangePicker.Label'),
        properties: {
          formatExp: t('global.settings.date.format'),
          localeName: cultureName,
          tz: timezone,
          mt: 3,
          btnLabel: t(
            'cardDetailsPage.CardTransactions.CardTransactionsFiltersDrawer.FormField.DateRangePicker.BtnLabel',
          ),
          filters: {
            rangeReference: {
              from: 'dateFrom',
              to: 'dateTo',
            },
            valueFormatter: (dateRange: { from: string; to: string }) =>
              dateRangeFormatter(dateRange.from, dateRange.to, dateFormat),
          },
        },
      },
      {
        type: 'fieldSet',
        key: 'fieldSet',
        label: 'Amount',
        components: [
          {
            key: 'amountFrom',
            type: 'floatNumber',
            name: 'amountFrom',
            label: t('cardDetailsPage.CardTransactions.CardTransactionsFiltersDrawer.FormField.FromAmount.Label'),
            properties: {
              variant: 'filled',
              allowNegative: false,
              mr: 2,
            },
          },
          {
            key: 'amountTo',
            type: 'floatNumber',
            name: 'amountTo',
            label: t('cardDetailsPage.CardTransactions.CardTransactionsFiltersDrawer.FormField.ToAmount.Label'),
            properties: {
              variant: 'filled',
              allowNegative: false,
            },
          },
        ],
        properties: {
          isRow: true,
          shouldGroupValidation: false,
          filters: {
            rangeReference: {
              from: 'amountFrom',
              to: 'amountTo',
            },
            valueFormatter: (amountRange: { from: number; to: number }) =>
              `${
                amountRange.from ? amountFormatter(amountRange.from, selectedCard?.availableBalance.currency) : ''
              } - ${amountRange.to ? amountFormatter(amountRange.to, selectedCard?.availableBalance.currency) : ''}`,
          },
        },
      },
      {
        key: 'status',
        type: 'select',
        name: 'status',
        label: t('cardDetailsPage.CardTransactions.CardTransactionsFiltersDrawer.FormField.Status.Label'),
        options: [
          { value: 'PENDING', label: 'Pending' },
          { value: 'FAILED', label: 'Failed' },
          { value: 'EXECUTED', label: 'Executed' },
        ],
        emptyOption: { value: STATUS_DEFAULT_VALUE, label: 'All' },
        properties: {
          variant: 'filled',
          filters: {
            valueFormatter: (statusValue: string) => (statusValue === STATUS_DEFAULT_VALUE ? 'All' : statusValue),
          },
        },
      },
      {
        key: 'merchant',
        type: 'text',
        label: t('cardDetailsPage.CardTransactions.CardTransactionsFiltersDrawer.FormField.Merchant.Label'),
        properties: {
          variant: 'filled',
        },
      },
    ],
    preloadedData: {
      amountFrom: filters?.amountFrom,
      amountTo: filters?.amountTo,
      transactionDate: [
        filters?.dateFrom ? new Date(filters.dateFrom) : null,
        filters?.dateTo ? new Date(filters.dateTo) : null,
      ],
      status: filters?.status ?? STATUS_DEFAULT_VALUE,
      merchant: filters?.merchant,
    },
  };
};

export { useCardTransactionsFiltersConfig };
